import { useEffect, useState } from 'react'
import { apiRequest } from '../api/client'

const ROLE_OPTIONS = [
  { value: 'reviewer',    label: 'Reviewer (external — OTP)' },
  { value: 'hq_reviewer', label: 'HQ Reviewer (brooklinen.com — SSO)' },
  { value: 'admin',       label: 'Admin' }
]

function StatusBadge({ disabled }) {
  if (disabled) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border bg-stone-100 text-stone-800 border-stone-300">
        Disabled
      </span>
    )
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border bg-emerald-50 text-emerald-800 border-emerald-200">
      Active
    </span>
  )
}

function DeleteConfirmModal({ user, onCancel, onConfirm, submitting, error }) {
  const [phrase, setPhrase] = useState('')
  const canSubmit = phrase === 'DELETE' && !submitting

  return (
    <div className="fixed inset-0 bg-slate-900/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <h3 className="text-lg font-semibold text-slate-900 mb-2">Delete user</h3>
        <p className="text-sm text-slate-700 mb-3">
          You are about to <strong className="text-rose-700">permanently delete</strong>{' '}
          <span className="font-mono">{user.email}</span>. This cannot be undone.
        </p>
        <p className="text-sm text-slate-600 mb-4">
          Past audit log entries and reviewer actions will be preserved with the user shown as
          "deleted user". The user's role and sign-in method will be lost. For test/dummy
          accounts only — for real teammates, use <strong>Disable</strong> instead.
        </p>
        <label className="label" htmlFor="delete-confirm">
          Type <span className="font-mono">DELETE</span> to confirm
        </label>
        <input
          id="delete-confirm"
          type="text"
          autoFocus
          autoComplete="off"
          value={phrase}
          onChange={(e) => setPhrase(e.target.value)}
          className="input w-full mb-3"
        />
        {error && (
          <div className="mb-3 p-2 rounded bg-rose-50 border border-rose-200 text-sm text-rose-800">
            {error}
          </div>
        )}
        <div className="flex justify-end gap-2">
          <button onClick={onCancel} disabled={submitting} className="btn-secondary">
            Cancel
          </button>
          <button
            onClick={() => onConfirm(phrase)}
            disabled={!canSubmit}
            className="px-3 py-2 text-sm font-medium rounded-md bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? 'Deleting…' : 'Delete user'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default function AdminUsersPage() {
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const [email, setEmail] = useState('')
  const [role, setRole] = useState('reviewer')
  const [submitting, setSubmitting] = useState(false)
  const [formError, setFormError] = useState('')
  const [notice, setNotice] = useState('')

  const [pendingDelete, setPendingDelete] = useState(null)
  const [deleteError, setDeleteError] = useState('')
  const [deleting, setDeleting] = useState(false)

  const [me, setMe] = useState(null)

  const loadUsers = () => {
    setLoading(true)
    apiRequest('/api/v1/admin/users')
      .then(({ users }) => setUsers(users))
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false))
  }

  useEffect(() => { loadUsers() }, [])

  useEffect(() => {
    apiRequest('/api/v1/me').then(({ user }) => setMe(user)).catch(() => {})
  }, [])

  const handleCreate = async (e) => {
    e.preventDefault()
    setFormError('')
    setNotice('')
    setSubmitting(true)
    try {
      const { user } = await apiRequest('/api/v1/admin/users', {
        method: 'POST',
        body: { user: { email, role } }
      })
      const authHint = user.auth_method === 'google_sso'
        ? 'They can sign in with Google.'
        : 'They can sign in with an email code.'
      setNotice(`Created ${user.email} as ${user.role}. ${authHint}`)
      setEmail('')
      setRole('reviewer')
      loadUsers()
    } catch (err) {
      setFormError(err.message)
    } finally {
      setSubmitting(false)
    }
  }

  const handleRoleChange = async (user, newRole) => {
    if (newRole === user.role) return
    try {
      await apiRequest(`/api/v1/admin/users/${user.id}`, {
        method: 'PATCH',
        body: { user: { role: newRole } }
      })
      loadUsers()
    } catch (err) {
      alert(err.message)
    }
  }

  const handleToggleDisable = async (user) => {
    const path = user.disabled ? 'enable' : 'disable'
    try {
      await apiRequest(`/api/v1/admin/users/${user.id}/${path}`, { method: 'PATCH' })
      loadUsers()
    } catch (err) {
      alert(err.message)
    }
  }

  const handleConfirmDelete = async (phrase) => {
    setDeleteError('')
    setDeleting(true)
    try {
      await apiRequest(`/api/v1/admin/users/${pendingDelete.id}`, {
        method: 'DELETE',
        body: { confirmation: phrase }
      })
      setPendingDelete(null)
      loadUsers()
    } catch (err) {
      setDeleteError(err.message)
    } finally {
      setDeleting(false)
    }
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
      <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-1">Users</h1>
      <p className="text-sm text-slate-600 mb-6">
        Pre-provision teammates. @brooklinen.com accounts sign in via Google SSO; other emails get a 6-digit OTP.
      </p>

      <div className="card p-5 sm:p-6 mb-8">
        <h2 className="text-sm font-semibold text-slate-900 uppercase tracking-wider mb-4">Create account</h2>

        {formError && (
          <div className="mb-4 p-3 rounded-md bg-rose-50 border border-rose-200 text-sm text-rose-800">{formError}</div>
        )}
        {notice && (
          <div className="mb-4 p-3 rounded-md bg-emerald-50 border border-emerald-200 text-sm text-emerald-800">{notice}</div>
        )}

        <form onSubmit={handleCreate} className="grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label className="label" htmlFor="new-email">Email</label>
            <input
              id="new-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="input"
              placeholder="name@brooklinen.com or external@vendor.com"
            />
          </div>
          <div>
            <label className="label" htmlFor="new-role">Role</label>
            <select
              id="new-role"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="input"
            >
              {ROLE_OPTIONS.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
          </div>
          <div className="sm:col-span-2">
            <button type="submit" disabled={submitting} className="btn-primary">
              {submitting ? 'Creating…' : 'Create account'}
            </button>
          </div>
        </form>
      </div>

      <div className="card overflow-hidden">
        <div className="px-5 sm:px-6 py-4 border-b border-slate-200">
          <h2 className="text-sm font-semibold text-slate-900 uppercase tracking-wider">All users</h2>
        </div>
        {loading ? (
          <p className="p-6 text-sm text-slate-600">Loading…</p>
        ) : error ? (
          <div className="p-5">
            <div className="p-3 rounded-md bg-rose-50 border border-rose-200 text-sm text-rose-800">{error}</div>
          </div>
        ) : users.length === 0 ? (
          <p className="p-6 text-sm text-slate-600">No users yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Email</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Role</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Auth</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {users.map((u) => {
                  const isSelf = me && me.id === u.id
                  return (
                    <tr key={u.id} className={u.disabled ? 'bg-slate-50/50' : ''}>
                      <td className="px-4 py-3 text-sm text-slate-900">
                        {u.email}
                        {isSelf && <span className="ml-2 text-xs text-slate-500">(you)</span>}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <select
                          value={u.role}
                          onChange={(e) => handleRoleChange(u, e.target.value)}
                          disabled={u.disabled}
                          className="text-sm border border-slate-300 rounded px-2 py-1 disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          {ROLE_OPTIONS.map((r) => (
                            <option key={r.value} value={r.value}>{r.label}</option>
                          ))}
                        </select>
                      </td>
                      <td className="px-4 py-3 text-sm"><StatusBadge disabled={u.disabled} /></td>
                      <td className="px-4 py-3 text-sm text-slate-600">{u.auth_method}</td>
                      <td className="px-4 py-3 text-right space-x-2 whitespace-nowrap">
                        <button
                          onClick={() => handleToggleDisable(u)}
                          disabled={isSelf}
                          title={isSelf ? "You can't disable your own account" : ''}
                          className="px-2 py-1 text-xs font-medium rounded border border-slate-300 text-slate-700 hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {u.disabled ? 'Enable' : 'Disable'}
                        </button>
                        <button
                          onClick={() => { setDeleteError(''); setPendingDelete(u) }}
                          disabled={isSelf}
                          title={isSelf ? "You can't delete your own account" : ''}
                          className="px-2 py-1 text-xs font-medium rounded border border-rose-300 text-rose-700 hover:bg-rose-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {pendingDelete && (
        <DeleteConfirmModal
          user={pendingDelete}
          submitting={deleting}
          error={deleteError}
          onCancel={() => setPendingDelete(null)}
          onConfirm={handleConfirmDelete}
        />
      )}
    </div>
  )
}
